import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaCamera, FaCheck, FaCloudUploadAlt, FaClipboardList, FaImages, FaLock, FaMapMarkerAlt, FaPhone, FaRedo, FaSave, FaSignInAlt, FaTrash, FaUndo } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import { formatDate, todayInput } from "../../utils/date.js";
import QueuedPhotoCard from "../components/QueuedPhotoCard.jsx";
import {
  deleteInspectionPhoto,
  finishInspectionJob,
  getInspectionJob,
  getInspectionJobSheet,
  inspectionPhotoContentUrl,
  listInspectionPhotos,
  reopenInspectionJob,
  saveInspectorNotes,
} from "../services/inspectionApi.js";
import {
  discardQueuedFinish,
  discardQueuedNotes,
  flushOutbox,
  queueFinish,
  queueNotes,
  queuedFinishFor,
  queuedNotesFor,
} from "../services/outbox.js";
import { discardQueuedPhoto, processUploadQueue, queuePhoto, retryFailedUploads } from "../services/uploadQueue.js";
import { cacheJob, cacheJobSheet, getCachedJob, listUploads } from "../storage/inspectionStore.js";

// A request that never got an answer is the offline case. A request the server
// ANSWERED (401 expired session, 403, 404) is not, and telling the inspector they
// are offline when they are signed out sends them looking for a signal that is
// already there.
const noAnswer = (error) => !error.response;
const sessionExpired = (error) => error.response?.status === 401;

function InspectionJobPage({ currentUser }) {
  const { inspectionId } = useParams();
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [job, setJob] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [queued, setQueued] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState("");   // "" | "offline" | "session"
  const [pendingFinish, setPendingFinish] = useState(null);
  const [pendingNotes, setPendingNotes] = useState(null);
  const [notes, setNotes] = useState(null);   // null until loaded, so typing is never overwritten
  const [savedNotes, setSavedNotes] = useState("");
  const [photosStale, setPhotosStale] = useState(false);
  const [error, setError] = useState("");

  const loadQueued = useCallback(() => {
    listUploads(currentUser.id, inspectionId).then(setQueued).catch(() => setQueued([]));
  }, [currentUser.id, inspectionId]);

  const loadPendingFinish = useCallback(() => {
    queuedFinishFor(currentUser.id, inspectionId).then(setPendingFinish).catch(() => setPendingFinish(null));
    queuedNotesFor(currentUser.id, inspectionId).then(setPendingNotes).catch(() => setPendingNotes(null));
  }, [currentUser.id, inspectionId]);

  // Offline the uploaded list cannot be refreshed, so say so rather than leave the
  // last known list looking current.
  const loadPhotos = useCallback(() => {
    if (!navigator.onLine) {
      setPhotosStale(true);
      return Promise.resolve();
    }
    return listInspectionPhotos(inspectionId)
      .then((rows) => {
        setPhotos(rows);
        setPhotosStale(false);
      })
      .catch(() => setPhotosStale(true));
  }, [inspectionId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getInspectionJob(inspectionId);
      setJob(result);
      setCached("");
      await cacheJob(currentUser.id, result);
    } catch (requestError) {
      // Still fall back to the saved copy — an inspector on site needs the address
      // and the notes whatever went wrong — but say WHICH thing went wrong.
      const saved = await getCachedJob(currentUser.id, inspectionId).catch(() => null);
      if (saved && (noAnswer(requestError) || sessionExpired(requestError))) {
        setJob(saved);
        setCached(noAnswer(requestError) ? "offline" : "session");
      } else {
        setError(requestError.response?.data?.error || "Unable to load this inspection.");
      }
    } finally {
      await Promise.all([loadPhotos(), loadQueued()]);
      loadPendingFinish();
      setLoading(false);
    }
  }, [currentUser.id, inspectionId, loadPhotos, loadPendingFinish, loadQueued]);

  useEffect(() => {
    load();
  }, [load]);

  // Seed the notes box ONCE (notes stays null until then, so a reload can never
  // overwrite what is being typed). A note still in the outbox was written here and
  // has not reached the server, so it wins over the server's copy.
  useEffect(() => {
    if (notes !== null || !job) return;
    const fromServer = job.notes_from_inspector || "";
    setSavedNotes(fromServer);
    setNotes(pendingNotes?.notes ?? fromServer);
  }, [job, notes, pendingNotes]);

  // Pull the job sheet down while there is a signal, whether or not they open it.
  // It replaces a piece of paper: paper works in a valley, so the sheet has to be
  // on the device BEFORE they drive there. Silent — nothing on this screen depends
  // on it.
  useEffect(() => {
    if (!navigator.onLine) return;
    getInspectionJobSheet(inspectionId)
      .then((sheet) => cacheJobSheet(currentUser.id, inspectionId, sheet))
      .catch(() => {});
  }, [currentUser.id, inspectionId]);

  useEffect(() => {
    let timer;
    function changed() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        loadQueued();
        loadPhotos();
        loadPendingFinish();
      }, 100);
    }
    window.addEventListener("inspection-queue-changed", changed);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("inspection-queue-changed", changed);
    };
  }, [loadPendingFinish, loadPhotos, loadQueued]);

  async function pickFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");

    // Queue each photo on its own. One unreadable file used to abandon the whole
    // selection, so picking three and getting one was indistinguishable from
    // everything having worked.
    const rejected = [];
    for (const file of files) {
      // An iPhone HEIC often arrives with an empty type — only reject a file we
      // positively know is not an image, and let the server judge the rest.
      if (file.type && !file.type.startsWith("image/")) {
        rejected.push(`${file.name} is not an image.`);
        continue;
      }
      try {
        await queuePhoto({ file, userId: currentUser.id, inspectionId });
      } catch (queueError) {
        rejected.push(`${file.name} could not be saved on this device: ${queueError.message}`);
      }
    }
    if (rejected.length) setError(rejected.join(" "));

    try {
      await loadQueued();
      // Start the uploads but do NOT wait for them. The photos are safely on the
      // device by now, which is the moment the buttons should come back — and with
      // real retry backoff a flaky link can keep a run alive for most of a minute.
      // The gallery and the queue refresh themselves through
      // inspection-queue-changed.
      processUploadQueue(currentUser.id).catch(() => {});
    } finally {
      await Promise.all([loadQueued(), loadPhotos()]);
      setBusy(false);
    }
  }

  // One Retry means "try everything again", the finish included.
  async function retry() {
    setBusy(true);
    setError("");
    try {
      // Same as picking photos: re-arm and let it run in the background rather than
      // holding the screen for the backoff waits.
      retryFailedUploads(currentUser.id).catch(() => {});
      await flushOutbox(currentUser.id);
    } finally {
      await Promise.all([loadQueued(), loadPhotos()]);
      loadPendingFinish();
      setBusy(false);
    }
  }

  async function discard(record) {
    if (!window.confirm("Remove this photo from the upload queue?")) return;
    await discardQueuedPhoto(record.id);
    await loadQueued();
  }

  // Finishing records the visit. It does NOT move the claim to Inspected — the
  // office does that once they have seen the photos.
  async function finish() {
    if (queued.length && !window.confirm(
      `${queued.length} photo${queued.length === 1 ? " is" : "s are"} still waiting to upload. ` +
      "They will keep uploading in the background, and the office will be told how " +
      "many are still coming. Finish this inspection anyway?"
    )) return;
    if (!queued.length && !photos.length && !window.confirm(
      "No photos have been added to this inspection. Finish it anyway?"
    )) return;

    setBusy(true);
    setError("");
    // Read the date HERE, on site, in the app timezone. Whether this goes now or
    // tomorrow morning from the queue, the inspection carries the day of the visit.
    const dateInspected = todayInput();
    try {
      setJob(await finishInspectionJob(inspectionId, {
        dateInspected,
        pendingUploadIds: queued.map((row) => row.id),
      }));
      await discardQueuedFinish(currentUser.id, inspectionId); // any earlier attempt is settled
    } catch (finishError) {
      if (noAnswer(finishError) || sessionExpired(finishError)) {
        // Keep the inspector moving: the finish is durable now and goes out with
        // the photos. This is the whole reason the outbox exists.
        await queueFinish({ userId: currentUser.id, inspectionId, dateInspected });
        setJob({ ...job, has_been_inspected: true, date_inspected: dateInspected });
      } else {
        setError(finishError.response?.data?.error || "Unable to finish this inspection.");
      }
    } finally {
      loadPendingFinish();
      setBusy(false);
    }
  }

  async function reopen() {
    setBusy(true);
    setError("");
    try {
      // A finish still sitting in the outbox never reached the server, so there is
      // nothing to undo there — drop the intent, or it would fire later and
      // re-finish the job the inspector just reopened.
      if (pendingFinish) {
        await discardQueuedFinish(currentUser.id, inspectionId);
        setJob({ ...job, has_been_inspected: false, date_inspected: null });
      } else {
        setJob(await reopenInspectionJob(inspectionId));
      }
    } catch (reopenError) {
      setError(reopenError.response?.data?.error || "Unable to reopen this inspection.");
    } finally {
      loadPendingFinish();
      setBusy(false);
    }
  }

  // Same shape as finishing: straight to the server when there is a signal, into the
  // outbox when there is not. Either way the inspector's words are safe the moment
  // they tap Save.
  async function saveNotes() {
    const text = (notes || "").trim();
    setBusy(true);
    setError("");
    try {
      setJob(await saveInspectorNotes(inspectionId, text));
      setSavedNotes(text);
      await discardQueuedNotes(currentUser.id, inspectionId);
    } catch (saveError) {
      if (noAnswer(saveError) || sessionExpired(saveError)) {
        await queueNotes({ userId: currentUser.id, inspectionId, notes: text });
        setSavedNotes(text);
      } else {
        setError(saveError.response?.data?.error || "Unable to save these notes.");
      }
    } finally {
      loadPendingFinish();
      setBusy(false);
    }
  }

  async function removeUploaded(photo) {
    if (!window.confirm("Delete this uploaded inspection photo?")) return;
    try {
      await deleteInspectionPhoto(inspectionId, photo.id);
      await loadPhotos();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || "Unable to delete photo.");
    }
  }

  if (loading) return <div className="text-center py-5 text-body-secondary"><Spinner size="sm" /> Loading inspection…</div>;
  if (!job) return <Alert variant="danger">{error || "Inspection not found."}</Alert>;

  const phone = job.mobile_phone || job.business_phone;
  const mapUrl = job.street_address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.street_address)}`
    : null;

  return (
    <div className="inspection-page mx-auto">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Link to="/inspection" className="btn btn-outline-secondary"><FaArrowLeft /></Link>
        <div className="flex-grow-1">
          <h1 className="h4 mb-0">Job #{job.claim_number}</h1>
          <div className="small text-body-secondary">Inspection #{job.seq} · {formatDate(job.scheduled_date, "long")}</div>
        </div>
        {job.has_been_inspected ? <Badge bg="success"><FaCheck className="me-1" />Finished</Badge> : null}
        {queued.length ? <Badge bg="warning" text="dark">{queued.length} waiting</Badge> : null}
      </div>

      {cached === "offline" ? (
        <Alert variant="warning">Showing saved job details while offline. Photos will stay queued until you reconnect.</Alert>
      ) : null}
      {cached === "session" ? (
        <Alert variant="danger" className="d-flex flex-wrap align-items-center gap-2">
          <div className="flex-grow-1">
            <strong>Your session has expired.</strong>
            <div className="small">
              Showing saved job details. Nothing on this device is lost — sign in again and
              your photos will send themselves.
            </div>
          </div>
          <Link to="/login" className="btn btn-sm btn-danger"><FaSignInAlt className="me-1" />Sign in</Link>
        </Alert>
      ) : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {job.has_been_inspected ? (
        <Alert variant={pendingFinish ? "warning" : "success"} className="d-flex flex-wrap align-items-center gap-2">
          <div className="flex-grow-1">
            <strong>Finished {job.date_inspected ? formatDate(job.date_inspected, "long") : ""}.</strong>
            {pendingFinish ? (
              <div className="small">
                <FaCloudUploadAlt className="me-1" />
                Saved on this device — it will be sent to the office as soon as you have
                a connection. The date above is the day you finished.
                {pendingFinish.error ? <div className="text-danger">{pendingFinish.error}</div> : null}
              </div>
            ) : (
              <div className="small">The office will check the photos and complete the job.</div>
            )}
          </div>
          <Button variant={pendingFinish ? "outline-warning" : "outline-success"} size="sm" onClick={reopen} disabled={busy}>
            <FaUndo className="me-1" />Reopen
          </Button>
        </Alert>
      ) : null}

      <Card className="mb-3 shadow-sm border-0">
        <Card.Body>
          <h2 className="h5">{job.client_name || job.contact_name || "Unnamed client"}</h2>
          {job.contact_name && job.contact_name !== job.client_name ? <div className="mb-2">Contact: {job.contact_name}</div> : null}
          {job.street_address ? (
            <a className="d-flex align-items-center gap-2 mb-2" href={mapUrl} target="_blank" rel="noreferrer">
              <FaMapMarkerAlt /> {job.street_address}
            </a>
          ) : null}
          {phone ? <a className="d-flex align-items-center gap-2 mb-2" href={`tel:${phone}`}><FaPhone /> {phone}</a> : null}
          {job.insurer_name ? <div className="small"><strong>Insurer:</strong> {job.insurer_name}</div> : null}
          {job.insurance_claim_number ? <div className="small"><strong>Claim reference:</strong> {job.insurance_claim_number}</div> : null}
          {job.assessor_ref ? <div className="small"><strong>Assessor reference:</strong> {job.assessor_ref}</div> : null}
          {job.notes_for_inspector ? <div className="bg-body-tertiary rounded p-2 mt-3 text-break"><strong>Notes:</strong><br />{job.notes_for_inspector}</div> : null}
        </Card.Body>
      </Card>

      {/* Everything else about the job lives one tap away, not on this screen —
          scope of works, assessor, lockbox, the health-and-safety reminder. */}
      <div className="d-grid mb-3">
        <Link to={`/inspection/${inspectionId}/sheet`} className="btn btn-outline-primary btn-lg">
          <FaClipboardList className="me-2" />Job Sheet
        </Link>
      </div>

      <div className="d-grid gap-2 d-sm-flex mb-3">
        {/* `multiple` is ignored on a phone (capture hands back one shot at a
            time) but matters on the desktop, where this input degrades to a
            plain file picker — without it a three-file test selection silently
            became one. */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={pickFiles} />
        <input ref={libraryRef} type="file" accept="image/*" multiple hidden onChange={pickFiles} />
        <Button size="lg" className="flex-grow-1" onClick={() => cameraRef.current?.click()} disabled={busy}>
          <FaCamera className="me-2" />Take Photo
        </Button>
        <Button size="lg" variant="outline-primary" onClick={() => libraryRef.current?.click()} disabled={busy}>
          <FaImages className="me-2" />Add Existing
        </Button>
        {queued.some((row) => row.status === "needs_retry" || row.status === "needs_auth") || pendingFinish?.error ? (
          <Button size="lg" variant="warning" onClick={retry} disabled={busy}><FaRedo className="me-2" />Retry</Button>
        ) : null}
      </div>

      {!job.has_been_inspected ? (
        <div className="d-grid mb-4">
          <Button size="lg" variant="success" onClick={finish} disabled={busy}>
            <FaCheck className="me-2" />Finish Inspection
          </Button>
          <div className="small text-body-secondary text-center mt-1">
            <FaLock className="me-1" />Records your visit. The office completes the job.
          </div>
        </div>
      ) : null}

      {/* Notes from site. The office's own notes field is separate and untouchable
          from here, so neither side can save over the other. */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Form.Label className="fw-semibold mb-1">Notes for the office</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes ?? ""}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Anything the office needs to know about this visit."
          />
          <div className="d-flex align-items-center gap-2 mt-2">
            <Button
              variant={(notes ?? "").trim() === savedNotes ? "outline-secondary" : "primary"}
              onClick={saveNotes}
              disabled={busy || (notes ?? "").trim() === savedNotes}
            >
              <FaSave className="me-1" />Save Notes
            </Button>
            {pendingNotes ? (
              <span className="small text-warning-emphasis">
                <FaCloudUploadAlt className="me-1" />
                Saved on this device — sending when you have a connection.
                {pendingNotes.error ? <span className="text-danger d-block">{pendingNotes.error}</span> : null}
              </span>
            ) : (notes ?? "").trim() === savedNotes && savedNotes ? (
              <span className="small text-success"><FaCheck className="me-1" />Sent to the office.</span>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {queued.length ? (
        <section className="mb-4">
          <h2 className="h5">Waiting on this device</h2>
          <div className="inspection-photo-grid">
            {queued.map((record) => (
              <QueuedPhotoCard key={record.id} record={record} onRetry={retry} onDiscard={() => discard(record)} />
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <h2 className="h5">
          Uploaded photos <Badge bg="secondary">{photos.length}</Badge>
        </h2>
        {photosStale ? (
          <div className="small text-body-secondary mb-2">
            Last known list — it will refresh when you are back online.
          </div>
        ) : null}
        {photos.length ? (
          <div className="inspection-photo-grid">
            {photos.map((photo) => (
              <Card key={photo.id} className="h-100">
                {/* The grid crops to a 4:3 tile, so tapping opens the real thing —
                    an inspector needs to see whether a shot is actually in focus
                    before they leave site. */}
                <a
                  href={inspectionPhotoContentUrl(inspectionId, photo.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-hint="Open full size"
                >
                  <Card.Img
                    variant="top"
                    src={inspectionPhotoContentUrl(inspectionId, photo.thumbnail_file_id || photo.id)}
                    alt={photo.original_filename || "Inspection photo"}
                    className="inspection-photo-thumb"
                  />
                </a>
                <Card.Body className="p-2 d-flex justify-content-between align-items-center gap-2">
                  <span className="small text-truncate">{photo.original_filename}</span>
                  <Button variant="outline-danger" size="sm" onClick={() => removeUploaded(photo)} data-hint="Delete photo" aria-label="Delete photo"><FaTrash /></Button>
                </Card.Body>
              </Card>
            ))}
          </div>
        ) : <div className="text-center text-body-secondary border rounded bg-body p-4">No uploaded photos yet.</div>}
      </section>
    </div>
  );
}

export default InspectionJobPage;
